import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';       // half day
import MoneyOffCsredIcon from '@mui/icons-material/MoneyOffCsred'; // no tips
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'; // no service
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const API_URL = 'https://ab.pizzahub.lk/api/salary_management.php';
const SMS_API = 'https://ab.pizzahub.lk/api/send_salary_sms.php';

const fmt = (n) => Number(n || 0).toFixed(2);
const approxEq = (a, b, eps = 0.01) => Math.abs(Number(a) - Number(b)) <= eps;

export default function SalaryManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [salarySlips, setSalarySlips] = useState([]);
  const [loading, setLoading] = useState(false);

  const [settlingId, setSettlingId] = useState(null);

  const [openSlip, setOpenSlip] = useState(false);
  const [slip, setSlip] = useState(null);

  // print CSS (80mm)
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page { size: 80mm auto; margin: 0; }
        body * { visibility: hidden; }
        .print-container, .print-container * { visibility: visible; }
        .print-container { position: absolute; top: 0; left: 0; width: 80mm; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const fetchData = () => {
    setLoading(true);
    fetch(`${API_URL}?year=${year}&month=${month}`)
      .then((r) => r.json())
      .then((data) => {
        setSalarySlips(Array.isArray(data) ? data : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [year, month]);

  const settleSalary = (s) => {
    if (s.settled) return;
    if (!window.confirm(`Mark ${s.staff_name} as settled?`)) return;
    setSettlingId(s.staff_id);

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'settle', year, month, staff_id: s.staff_id })
    })
      .then((r) => r.json())
      .then((d) => {
        setSettlingId(null);
        if (d.status === 'success') fetchData();
        else alert('Error: ' + d.message);
      })
      .catch((err) => {
        console.error(err);
        setSettlingId(null);
        alert('Network error');
      });
  };

  const sendSalarySMS = (slip) => {
    fetch(SMS_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: slip.staff_phone,
        staff_id: slip.staff_id,
        staff_name: slip.staff_name,
        total_salary: slip.total_salary,
        year,
        month
      })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === 'sent') alert(`SMS sent to ${slip.staff_name}`);
        else alert('SMS error: ' + d.sms_response);
      })
      .catch(() => alert('Network error'));
  };

  const showSlip = (s) => {
    setSlip(s);
    setOpenSlip(true);
  };
  const closeSlip = () => {
    setSlip(null);
    setOpenSlip(false);
  };

  // Derived helpers for a given slip
  const summarizeSlip = (s) => {
    const perDay = Number(s.basic_salary_per_day || 0);
    const rows = Array.isArray(s.daily_breakdown) ? s.daily_breakdown : [];

    let halfDays = 0;
    let noTipDays = 0;
    let noSvcDays = 0;
    let tipTotal = 0;
    let svcTotal = 0;

    rows.forEach((d) => {
      const flagHalf =
        typeof d.flag_half !== 'undefined'
          ? !!d.flag_half
          : perDay > 0 && approxEq(d.daily_basic, perDay / 2);

      const flagNoTips =
        typeof d.flag_no_tips !== 'undefined'
          ? !!d.flag_no_tips
          : Number(d.daily_tip) === 0;

      const flagNoSvc =
        typeof d.flag_no_svc !== 'undefined'
          ? !!d.flag_no_svc
          : Number(d.daily_svc) === 0;

      if (flagHalf) halfDays++;
      if (flagNoTips) noTipDays++;
      if (flagNoSvc) noSvcDays++;

      tipTotal += Number(d.daily_tip) || 0;
      svcTotal += Number(d.daily_svc) || 0;
    });

    return {
      halfDays,
      noTipDays,
      noSvcDays,
      tipTotal,
      svcTotal
    };
  };

  const Legend = () => (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        p: 1,
        borderRadius: 2,
        bgcolor: 'background.paper'
      }}
    >
      <Chip
        icon={<AccessTimeIcon />}
        label="Half Day applied"
        size="small"
        variant="outlined"
      />
      <Chip
        icon={<MoneyOffCsredIcon />}
        label="No Tips"
        size="small"
        variant="outlined"
        color="warning"
      />
      <Chip
        icon={<DoNotDisturbAltIcon />}
        label="No Service"
        size="small"
        variant="outlined"
        color="error"
      />
      <Tooltip title="A zero tips/service value could mean the pool was zero that day or the staff was excluded. If your backend sets flag_no_tips / flag_no_svc, we show the exact reason.">
        <InfoOutlinedIcon fontSize="small" style={{ opacity: 0.7 }} />
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Salary Management
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Year"
          size="small"
          type="number"
          value={year}
          onChange={(e) => setYear(+e.target.value)}
        />
        <TextField
          label="Month"
          size="small"
          type="number"
          inputProps={{ min: 1, max: 12 }}
          value={month}
          onChange={(e) => setMonth(+e.target.value)}
        />
        <Button startIcon={<RefreshIcon />} variant="contained" onClick={fetchData}>
          Refresh
        </Button>
        <Legend />
      </Box>

      {/* Salary List */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : salarySlips.length === 0 ? (
        <Typography>No data for {month}/{year}.</Typography>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {salarySlips.map((s) => {
            const sum = summarizeSlip(s);
            return (
              <Card key={s.staff_id} variant="outlined" sx={{ overflow: 'hidden' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{s.staff_name}</Typography>
                    <Chip
                      size="small"
                      label={s.settled ? 'Settled' : 'Pending'}
                      color={s.settled ? 'success' : 'default'}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Summary bullets */}
                  <Stack spacing={0.5}>
                    <Typography><b>Days Present</b>: {s.days_present}</Typography>
                    <Typography><b>Basic</b>: Rs {fmt(s.basic_earning)}</Typography>
                    <Typography><b>Tips</b>: Rs {fmt(sum.tipTotal)} &nbsp;|&nbsp; <b>Service</b>: Rs {fmt(sum.svcTotal)}</Typography>
                    <Typography sx={{ color: 'green' }}><b>Bonus</b>: +Rs {fmt(s.bonus_total)}</Typography>
                    <Typography sx={{ color: 'red' }}><b>Penalties</b>: −Rs {fmt(s.penalty_total)}</Typography>
                    <Typography><b>Loan</b>: −Rs {fmt(s.loan_deduction)}</Typography>
                    <Typography><b>Advance</b>: −Rs {fmt(s.advance_deduction)}</Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      Net: Rs {fmt(s.total_salary)}
                    </Typography>
                  </Stack>

                  {/* Flags summary */}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>Applied Options (Counts)</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip icon={<AccessTimeIcon />} label={`Half Days: ${sum.halfDays}`} size="small" />
                    <Chip icon={<MoneyOffCsredIcon />} label={`No Tips Days: ${sum.noTipDays}`} size="small" color="warning" />
                    <Chip icon={<DoNotDisturbAltIcon />} label={`No Service Days: ${sum.noSvcDays}`} size="small" color="error" />
                  </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button size="small" startIcon={<ReceiptLongIcon />} variant="outlined" onClick={() => showSlip(s)}>
                    View Slip
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => sendSalarySMS(s)}>
                      SMS Salary Sheet
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={s.settled || settlingId === s.staff_id}
                      onClick={() => settleSalary(s)}
                    >
                      {settlingId === s.staff_id
                        ? 'Settling…'
                        : s.settled
                          ? 'Settled'
                          : 'Settle'}
                    </Button>
                  </Stack>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff</TableCell>
                <TableCell align="right">Days</TableCell>
                <TableCell align="right">Basic</TableCell>
                <TableCell align="right">Tips</TableCell>
                <TableCell align="right">Service</TableCell>
                <TableCell align="right">Bonus</TableCell>
                <TableCell align="right">Penalties</TableCell>
                <TableCell align="right">Loan</TableCell>
                <TableCell align="right">Advance</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salarySlips.map((s) => {
                const sum = summarizeSlip(s);
                return (
                  <TableRow key={s.staff_id}>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography sx={{ fontWeight: 600 }}>{s.staff_name}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" icon={<AccessTimeIcon />} label={`Half: ${sum.halfDays}`} />
                          <Chip size="small" icon={<MoneyOffCsredIcon />} label={`No Tips: ${sum.noTipDays}`} color="warning" />
                          <Chip size="small" icon={<DoNotDisturbAltIcon />} label={`No Svc: ${sum.noSvcDays}`} color="error" />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{s.days_present}</TableCell>
                    <TableCell align="right">{fmt(s.basic_earning)}</TableCell>
                    <TableCell align="right">{fmt(sum.tipTotal)}</TableCell>
                    <TableCell align="right">{fmt(sum.svcTotal)}</TableCell>
                    <TableCell align="right" sx={{ color: 'green' }}>{fmt(s.bonus_total)}</TableCell>
                    <TableCell align="right" sx={{ color: 'red' }}>{fmt(s.penalty_total)}</TableCell>
                    <TableCell align="right">{fmt(s.loan_deduction)}</TableCell>
                    <TableCell align="right">{fmt(s.advance_deduction)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(s.total_salary)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button size="small" variant="outlined" onClick={() => showSlip(s)}>View Slip</Button>
                        <Button size="small" variant="outlined" onClick={() => sendSalarySMS(s)}>SMS Salary Sheet</Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={s.settled || settlingId === s.staff_id}
                          onClick={() => settleSalary(s)}
                        >
                          {settlingId === s.staff_id
                            ? 'Settling…'
                            : s.settled
                              ? 'Settled'
                              : 'Settle'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Slip Dialog */}
      <Dialog open={openSlip} onClose={closeSlip} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            Salary Slip
            <IconButton onClick={() => window.print()} size="small">
              <PrintIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {slip && (
            <Box className="print-container" sx={{ p: 1 }}>
              {/* Header */}
              <Typography variant="h6" align="center">{slip.staff_name}</Typography>
              <Typography align="center" color="text.secondary" gutterBottom>
                Period: {String(month).padStart(2, '0')}/{year}
              </Typography>

              {/* Summary Cards */}
              <Paper variant="outlined" sx={{ my: 2, p: 2 }}>
                {(() => {
                  const sum = summarizeSlip(slip);
                  const tipTotal = sum.tipTotal;
                  const svcTotal = sum.svcTotal;
                  return (
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Days Present</TableCell>
                          <TableCell align="right">{slip.days_present}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Basic Earned</TableCell>
                          <TableCell align="right">Rs {fmt(slip.basic_earning)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Tips Share</TableCell>
                          <TableCell align="right">Rs {fmt(tipTotal)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Service‐Charge Share</TableCell>
                          <TableCell align="right">Rs {fmt(svcTotal)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: 'green' }}>Bonus</TableCell>
                          <TableCell align="right" sx={{ color: 'green' }}>+ Rs {fmt(slip.bonus_total)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: 'red' }}>Penalties</TableCell>
                          <TableCell align="right" sx={{ color: 'red' }}>− Rs {fmt(slip.penalty_total)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Loan Deduction</TableCell>
                          <TableCell align="right">− Rs {fmt(slip.loan_deduction)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Advance Deduction</TableCell>
                          <TableCell align="right">− Rs {fmt(slip.advance_deduction)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><b>Net Pay</b></TableCell>
                          <TableCell align="right"><b>Rs {fmt(slip.total_salary)}</b></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  );
                })()}
              </Paper>

              {/* Option counts */}
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                {(() => {
                  const sum = summarizeSlip(slip);
                  return (
                    <>
                      <Chip icon={<AccessTimeIcon />} label={`Half Days: ${sum.halfDays}`} size="small" />
                      <Chip icon={<MoneyOffCsredIcon />} label={`No Tips: ${sum.noTipDays}`} size="small" color="warning" />
                      <Chip icon={<DoNotDisturbAltIcon />} label={`No Service: ${sum.noSvcDays}`} size="small" color="error" />
                    </>
                  );
                })()}
              </Stack>

              {/* Daily Breakdown */}
              <Typography variant="subtitle1" gutterBottom>Daily Breakdown</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Basic</TableCell>
                    <TableCell align="right">Tips</TableCell>
                    <TableCell align="right">Svc (50%)</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Flags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slip.daily_breakdown.map((d, i) => {
                    const perDay = Number(slip.basic_salary_per_day || 0);
                    const flagHalf =
                      typeof d.flag_half !== 'undefined'
                        ? !!d.flag_half
                        : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
                    const flagNoTips =
                      typeof d.flag_no_tips !== 'undefined'
                        ? !!d.flag_no_tips
                        : Number(d.daily_tip) === 0;
                    const flagNoSvc =
                      typeof d.flag_no_svc !== 'undefined'
                        ? !!d.flag_no_svc
                        : Number(d.daily_svc) === 0;

                    return (
                      <TableRow key={i}>
                        <TableCell>{d.date}</TableCell>
                        <TableCell align="right">{fmt(d.daily_basic)}</TableCell>
                        <TableCell align="right">{fmt(d.daily_tip)}</TableCell>
                        <TableCell align="right">{fmt(d.daily_svc)}</TableCell>
                        <TableCell align="right">{fmt(d.daily_total)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {flagHalf && (
                              <Chip
                                size="small"
                                icon={<AccessTimeIcon />}
                                label="Half Day"
                                variant="outlined"
                              />
                            )}
                            {flagNoTips && (
                              <Tooltip title={d.flag_no_tips === false ? 'No tips (pool was zero)' : 'Tips excluded'}>
                                <Chip
                                  size="small"
                                  color="warning"
                                  icon={<MoneyOffCsredIcon />}
                                  label="No Tips"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            {flagNoSvc && (
                              <Tooltip title={d.flag_no_svc === false ? 'No service (pool was zero)' : 'Service excluded'}>
                                <Chip
                                  size="small"
                                  color="error"
                                  icon={<DoNotDisturbAltIcon />}
                                  label="No Service"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            {!flagHalf && !flagNoTips && !flagNoSvc && <Typography variant="caption" color="text.secondary">—</Typography>}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Advance history inline (if present) */}
              {Array.isArray(slip.advance_payments) && slip.advance_payments.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Advance History</Typography>
                  <Stack spacing={0.5}>
                    {slip.advance_payments.map((a, idx) => (
                      <Typography key={idx} variant="body2">
                        {a.date}: Rs {fmt(a.amount)}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSlip}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
